import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import api from '../api/client';

// Andamento tornei amatoriali in dashboard (17/10/2026, titolare): per stagione sportiva (1/9–31/8)
// incassato rispetto alle spese inserite nella Contabilità tornei. Solo statistica, non è la banca.
const eur = (n) => (n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: (n || 0) % 1 ? 2 : 0 });
const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const gg = (iso) => { const [, m, d] = iso.split('-'); return `${+d} ${MESI[+m - 1]}`; };
const COL_INCASSATO = '#F4600C', COL_SPESE = '#8E8E93';

export default function AndamentoTornei() {
  const nav = useNavigate();
  const [dati, setDati] = useState(null);
  const [stagione, setStagione] = useState('');
  useEffect(() => {
    api.get('/tornei/andamento', { params: stagione ? { stagione } : {} }).then(r => setDati(r.data)).catch(() => setDati(false));
  }, [stagione]);
  if (dati === false) return null;
  if (!dati) return <div style={s.card}><div style={s.titolo}>ANDAMENTO TORNEI</div><div style={s.vuoto}>Caricamento…</div></div>;
  const t = dati.totali;
  const grafico = dati.tornei.map(x => ({ ...x, etichetta: `${gg(x.data)} · ${x.nome.length > 18 ? x.nome.slice(0, 17) + '…' : x.nome}` }));
  const senzaSpese = dati.tornei.filter(x => x.senza_spese).length;

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div style={{ ...s.titolo, marginBottom: 0 }}><Trophy size={14} style={{ marginRight: '8px' }} />ANDAMENTO TORNEI</div>
        {dati.stagioni.length > 1 ? (
          <select style={s.select} value={dati.stagione} onChange={e => setStagione(e.target.value)}>
            {dati.stagioni.map(a => <option key={a} value={a}>Stagione {a}/{a + 1}</option>)}
          </select>
        ) : <span style={s.dim}>Stagione {dati.stagione}/{dati.stagione + 1}</span>}
      </div>
      {t.tornei === 0 ? <div style={s.vuoto}>Nessun torneo in questa stagione.</div> : <>
        <div style={s.tiles}>
          {[['INCASSATO', t.incassato, null], ['SPESE', t.spese, null], ['RISULTATO', t.risultato, t.risultato < 0 ? '#B42318' : '#15803D']].map(([l, v, c]) => (
            <div key={l} style={s.tile}><div style={s.tileLabel}>{l}</div><div style={{ ...s.tileValore, color: c || 'var(--text)' }}>{eur(v)}</div></div>
          ))}
        </div>
        <div style={s.dim}>{t.tornei} {t.tornei === 1 ? 'torneo' : 'tornei'} · {t.partecipanti} iscrizioni{t.da_incassare > 0 ? ` · ancora da incassare ${eur(t.da_incassare)}` : ''}</div>
        <ResponsiveContainer width="100%" height={Math.max(160, grafico.length * 56 + 40)}>
          <BarChart data={grafico} layout="vertical" margin={{ top: 12, left: 0, right: 16, bottom: 0 }} barGap={2}
            onClick={e => e?.activePayload?.[0] && nav(`/contabilita-tornei?torneo=${e.activePayload[0].payload.id}`)}>
            <CartesianGrid horizontal={false} stroke="var(--border)" />
            <XAxis type="number" tick={{ fill: 'var(--text-dim)', fontSize: 11 }} tickFormatter={v => `€${v}`} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="etichetta" width={150} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: 'var(--bg-input)' }} content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const x = payload[0].payload;
              return (
                <div style={s.tooltip}>
                  <b>{x.nome}</b><div style={s.dim}>{gg(x.data)} · {x.tipologia} · {x.partecipanti} iscritti{x.chiuso ? ' · chiuso' : ''}</div>
                  <div>Incassato <b>{eur(x.incassato)}</b>{x.da_incassare > 0 && <span style={s.dim}> (+{eur(x.da_incassare)} da incassare)</span>}</div>
                  <div>Spese <b>{eur(x.spese)}</b>{x.senza_spese && <span style={s.dim}> (non inserite)</span>}</div>
                  <div>Risultato <b style={{ color: x.risultato < 0 ? '#B42318' : '#15803D' }}>{eur(x.risultato)}</b></div>
                </div>
              );
            }} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
            <Bar dataKey="incassato" name="Incassato" fill={COL_INCASSATO} radius={[0, 4, 4, 0]} barSize={14} cursor="pointer" />
            <Bar dataKey="spese" name="Spese" fill={COL_SPESE} radius={[0, 4, 4, 0]} barSize={14} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
        {dati.per_voce.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={s.tileLabel}>SPESE PER VOCE</div>
            {dati.per_voce.map(v => (
              <div key={v.voce} style={s.riga}><span>{v.voce}</span><b>{eur(v.importo)}</b></div>
            ))}
          </div>
        )}
        {senzaSpese > 0 && <div style={{ ...s.dim, marginTop: '8px' }}>{senzaSpese === 1 ? '1 torneo non ha' : `${senzaSpese} tornei non hanno`} ancora le spese inserite: il risultato è solo l'incassato.</div>}
      </>}
    </div>
  );
}

const s = {
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  titolo: { fontSize: 'var(--font-size-xs)', fontWeight: '700', letterSpacing: '2px', color: 'var(--text-muted)', marginBottom: '16px', display: 'flex', alignItems: 'center' },
  vuoto: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '24px 0' },
  select: { background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 10px', color: 'var(--text)', fontSize: '13px' },
  dim: { color: 'var(--text-dim)', fontSize: '12.5px' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '8px' },
  tile: { background: 'var(--bg-input)', borderRadius: '10px', padding: '12px 14px' },
  tileLabel: { fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', color: 'var(--text-dim)', marginBottom: '4px' },
  tileValore: { fontSize: '22px', fontWeight: 800, fontFamily: "'Barlow Condensed', sans-serif" },
  tooltip: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '3px', boxShadow: '0 6px 20px rgba(0,0,0,.12)' },
  riga: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13.5px' },
};
